import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireEmailVerification?: boolean;
  requiredRole?: 'USER' | 'ADMIN' | 'SUPER_ADMIN';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireEmailVerification = false,
  requiredRole,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to="/auth/login"
        state={{ from: location }}
        replace
      />
    );
  }

  // Check email verification requirement
  if (requireEmailVerification && !user.email_verified) {
    return (
      <Navigate
        to="/auth/verify-email"
        state={{ from: location }}
        replace
      />
    );
  }

  // Check role requirement
  if (requiredRole) {
    const hasRequiredRole = () => {
      // SUPER_ADMIN can access everything
      if (user.role === 'SUPER_ADMIN') return true;
      
      // ADMIN can access ADMIN and USER required pages
      if (user.role === 'ADMIN' && (requiredRole === 'ADMIN' || requiredRole === 'USER')) return true;
      
      // USER can only access USER required pages
      if (user.role === 'USER' && requiredRole === 'USER') return true;
      
      // Also check for lowercase variants (for backward compatibility)
      if (user.role?.toLowerCase() === 'admin' && requiredRole === 'ADMIN') return true;
      if (user.role?.toLowerCase() === 'super_admin' && requiredRole === 'SUPER_ADMIN') return true;
      
      // Check for 'admin' string (common in many systems)
      if (user.role === 'admin' && requiredRole === 'ADMIN') return true;
      
      return false;
    };

    if (!hasRequiredRole()) {
      // For admin routes, show 403 instead of redirecting
      if (requiredRole === 'ADMIN' || requiredRole === 'SUPER_ADMIN') {
        return (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                접근 권한이 없습니다
              </h1>
              <p className="text-gray-600">
                이 페이지에 접근할 권한이 없습니다.
              </p>
            </div>
          </div>
        );
      }
      
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};